/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { CheckCircle2, XCircle } from "lucide-react";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";

// DEMO placeholder — actual match id now comes from useMatch() at runtime

export default function ReadinessPage() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;

  const [rows, setRows] = useState([]);
  const [match, setMatch] = useState(null);
  const [preMatchDepts, setPreMatchDepts] = useState([]);

  const load = async () => {
    if (!DEMO) return;
    const [{ data: m }, { data: r }, { data: d }] = await Promise.all([
      api.get(`/matches/${DEMO}`),
      api.get(`/matches/${DEMO}/readiness`),
      api.get(`/reference/pre-match-departments?match_id=${DEMO}`),
    ]);
    setMatch(m);
    setRows(r);
    setPreMatchDepts(d.departments || []);
  };
  useEffect(() => { load(); }, [DEMO]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (dept, ready, notes) => {
    await api.post(`/matches/${DEMO}/readiness`, { department: dept, ready, notes: notes || "" });
    load();
  };

  if (!selectedMatchId) return <RequireMatch />;
  if (!match) return <div className="p-8 text-zinc-500">Loading…</div>;

  // Union of pre-match departments + custom depts saved locally + any dept already certified
  let customDepts = [];
  try { customDepts = JSON.parse(localStorage.getItem("mc_custom_depts") || "[]"); } catch { customDepts = []; }
  const certifiedDepts = rows.map((r) => r.department);
  const allDepartments = Array.from(
    new Set([
      ...(preMatchDepts || []),
      ...customDepts,
      ...certifiedDepts,
    ])
  )
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const map = Object.fromEntries(rows.map((r) => [r.department, r]));
  const total = allDepartments.length;
  const ready = allDepartments.filter((d) => map[d]?.ready).length;
  const pct = total ? Math.round((ready / total) * 100) : 0;
  const allReady = total > 0 && ready === total;

  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-readiness">
      <div className="border-b border-cmdBorder pb-6 mb-6">
        <div className="mc-label">Departmental Sign-off</div>
        <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Readiness Certificates</h1>
        <p className="text-zinc-400 mt-3 text-sm max-w-2xl">Each department confirms operational readiness. When all are green, the Match Director may issue the overall <span className="text-readyGreen font-semibold">MATCH READY</span> certificate.</p>
      </div>

      <div className="mc-panel p-5 mb-6" data-testid="readiness-summary">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div>
            <div className="mc-label">Overall</div>
            <div className="mc-metric text-6xl mt-2" style={{ color: allReady ? "#34C759" : "#FFCC00" }}>{ready}/{total}</div>
          </div>
          <div className="flex-1 md:mx-8">
            <div className="w-full h-4 border border-cmdBorder bg-black relative">
              <div className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, backgroundColor: allReady ? "#34C759" : "#FFCC00" }} />
            </div>
            <div className="font-mono-live text-xs text-zinc-400 mt-2">{pct}% CERTIFIED</div>
          </div>
          <div>
            <div className={`border p-4 text-center ${allReady ? "border-readyGreen text-readyGreen" : "border-cmdBorder text-zinc-500"}`}>
              <div className="mc-label">Match Ready</div>
              <div className="font-head font-black uppercase text-2xl mt-1">{allReady ? "CLEARED" : "PENDING"}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {allDepartments.map((d) => {
          const cert = map[d];
          const isReady = !!cert?.ready;
          return (
            <div key={d} className="mc-panel p-4 flex items-center justify-between" data-testid={`readiness-dept-${d}`}>
              <div>
                <div className="font-medium">{d}</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {cert?.ready ? `Submitted by ${cert.submitted_by}` : "Not submitted"}
                </div>
                {cert?.notes && <div className="text-xs text-warnAmber mt-1">{cert.notes}</div>}
              </div>
              <button
                onClick={() => toggle(d, !isReady, cert?.notes)}
                className={`flex items-center gap-1 border px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
                  isReady ? "border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-black" : "border-cmdBorder text-zinc-400 hover:border-white"
                }`}
                data-testid={`readiness-toggle-${d}`}
              >
                {isReady ? <><CheckCircle2 className="w-3 h-3" /> Ready</> : <><XCircle className="w-3 h-3" /> Not Ready</>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
